import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';

const AdminCouponManager = () => {
  const { token } = useAuth(); // Get the token from Auth context
  const [coupons, setCoupons] = useState([]);
  const [formData, setFormData] = useState({
    couponCode: '',
    discountPercentage: '',
    status: 'active', // Default status
    limit: '', // Add limit field
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Fetch existing coupons
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const response = await api.get('/coupons', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCoupons(response.data);
      } catch (err) {
        console.error(err);
        setError('Failed to fetch coupons.');
      }
    };
    fetchCoupons();
  }, [token]);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    try {
      const response = await api.post('/coupons', formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCoupons([...coupons, response.data]); // Update coupon list
      setMessage('Coupon created successfully!');
      setFormData({ couponCode: '', discountPercentage: '', status: 'active', limit: '' });
    } catch (err) {
      console.error(err);
      setError('Failed to create coupon.');
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (couponId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

    try {
      await api.put(`/coupons/${couponId}`, { status: newStatus });
      setCoupons((prevCoupons) =>
        prevCoupons.map((coupon) =>
          coupon._id === couponId ? { ...coupon, status: newStatus } : coupon
        )
      );
    } catch (error) {
      console.error('Failed to update coupon status:', error);
      setError('Failed to update status.');
    }
  };

  // Handle delete
  const handleDelete = async (couponId) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        await api.delete(`/coupons/${couponId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCoupons(coupons.filter((coupon) => coupon._id !== couponId));
        setMessage('Coupon deleted successfully!');
      } catch (err) {
        console.error(err);
        setError('Failed to delete coupon.');
      }
    }
  };

  return (
    <div className="flex flex-col md:flex-row">
      {/* Sidebar */}
      <Sidebar />

      {/* Content */}
      <div className="flex-1 bg-gray-50 p-4 md:p-6">
        <h1 className="text-lg md:text-2xl font-bold text-gray-700 mb-4">Set Promo Code</h1>

        {/* Promo Code Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Create New Coupon</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Coupon Code */}
            <div>
              <label className="block mb-1 font-medium text-gray-700">Promo Code</label>
              <input
                type="text"
                name="couponCode"
                value={formData.couponCode}
                onChange={handleChange}
                required
                className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring focus:border-primary"
              />
            </div>

            {/* Discount Percentage */}
            <div>
              <label className="block mb-1 font-medium text-gray-700">Discount %</label>
              <input
                type="number"
                name="discountPercentage"
                value={formData.discountPercentage}
                onChange={handleChange}
                required
                className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring focus:border-primary"
              />
            </div>

            {/* Usage Limit */}
            <div>
              <label className="block mb-1 font-medium text-gray-700">Usage Limit</label>
              <input
                type="number"
                name="limit"
                value={formData.limit}
                onChange={handleChange}
                required
                className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring focus:border-primary"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block mb-1 font-medium text-gray-700">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="border border-gray-300 rounded-lg p-2 w-full focus:outline-none focus:ring focus:border-primary"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <button
            type="submit"
            className="bg-primary text-black px-4 py-2 rounded-lg hover:bg-yellow-400 mt-4"
          >
            Create Coupon
          </button>
        </form>

        {message && <p className="text-green-600 mb-4">{message}</p>}
        {error && <p className="text-red-600 mb-4">{error}</p>}

        {/* Existing Coupons */}
        <h2 className="text-lg font-semibold mb-4">Active Codes</h2>
        <div className="bg-white rounded-lg shadow-md p-6">
          {coupons.length === 0 ? (
            <p className="text-gray-600">No coupons available.</p>
          ) : (
            <table className="min-w-full bg-white border-collapse border border-gray-200">
              <thead>
                <tr className="bg-gray-100 text-gray-700">
                  <th className="py-2 px-4 border">Name</th>
                  <th className="py-2 px-4 border">Discount (%)</th>
                  <th className="py-2 px-4 border">Usage Limit</th>
                  <th className="py-2 px-4 border">Status</th>
                  <th className="py-2 px-4 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr key={coupon._id} className="text-gray-600">
                    <td className="py-2 px-4 border">{coupon.couponCode}</td>
                    <td className="py-2 px-4 border">{coupon.discountPercentage}</td>
                    <td className="py-2 px-4 border">
                      {coupon.limit === 0 ? "Unlimited" : coupon.limit}
                    </td>
                    <td className="py-2 px-4 border">{coupon.status}</td>
                    <td className="py-2 px-4 border">
                      <button
                        onClick={() => handleStatusToggle(coupon._id, coupon.status)}
                        className={`px-3 py-1 text-white rounded-lg ${coupon.status === 'active'
                          ? 'bg-red-500 hover:bg-red-600'
                          : 'bg-green-500 hover:bg-green-600'
                          }`}
                      >
                        {coupon.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDelete(coupon._id)}
                        className="px-3 py-1 bg-gray-300 hover:bg-gray-400 rounded-lg ml-2"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCouponManager;
